/**
 * Tenant Config API Service
 * @description Tenant 설정 API 연동 (카탈로그, 워크플로우 등)
 */

import { config } from '@shared/config';
import { getTenantAuthStorage } from '@/utils/authStorage';

const MONITORING_REQUEST_TIMEOUT_MS = 20000; // 20초 (모니터링 API 무한 대기 방지)

type ApiRequestOptions = RequestInit & { timeout?: number };

/**
 * API 요청 헬퍼 함수
 */
async function apiRequest<T>(
    endpoint: string,
    options: ApiRequestOptions = {}
): Promise<T> {
    const { timeout, ...fetchOptions } = options;
    const storage = getTenantAuthStorage();
    const token = storage.getToken();
    const user = storage.getUser();

    let tenantId = '';
    if (user?.userType === 'tenant-user') {
        tenantId = user.tenantId;
    }

    const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...fetchOptions.headers,
    };

    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    if (tenantId) {
        headers['X-Tenant-Id'] = tenantId;
    }

    const controller = timeout != null ? new AbortController() : undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (controller && (timeout ?? 0) > 0) {
        timeoutId = setTimeout(() => controller.abort(), timeout ?? 0);
    }

    try {
        const response = await fetch(`${config.apiUrl}/api/portal${endpoint}`, {
            ...fetchOptions,
            headers,
            signal: controller?.signal,
        });

        if (timeoutId != null) clearTimeout(timeoutId);

        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: 'API 요청 실패' }));
            throw new Error(error.message || `API Error: ${response.status}`);
        }

        return response.json();
    } catch (e) {
        if (timeoutId != null) clearTimeout(timeoutId);
        if (e instanceof Error && e.name === 'AbortError') {
            throw new Error('요청 시간이 초과되었습니다. 서버 상태를 확인해 주세요.');
        }
        throw e;
    }
}

// 시스템 카탈로그
export interface System {
    systemId: string;
    name: string;
    category: string;
    description?: string;
}

// ITSM 서비스
export interface ITSMService {
    serviceId: string;
    name: string;
    category: string;
    description?: string;
    statusOptions?: string[];
}

// 요청 타입
export interface RequestType {
    requestTypeId: string;
    domain: string;
    category: string;
    displayName: string;
    description?: string;
    workflowServiceTypeId?: string;
    form: {
        fields: FormField[];
    };
    approvalFlowId?: string;
    /** 해당 도메인(서비스 요청/장애/변경)에서 노출 여부. 없으면 전체 사용 */
    enabledInDomains?: ('servicerequest' | 'incident' | 'changerequest')[];
}

// Workflow 서비스 유형 (Workflow 설정에서 관리)
export interface WorkflowServiceType {
    serviceTypeId: string;
    displayName: string;
    description?: string;
    linkedFolder?: string;
    displayOrder?: number;
    enabledInDomains?: ('servicerequest' | 'incident' | 'changerequest')[];
}

// Tenant 설정 응답
export interface TenantConfig {
    tenant: {
        tenantId: string;
        tenantName: string;
    };
    catalogs: {
        systems: System[];
        itsmServices: ITSMService[];
    };
    workflows: {
        serviceTypes: WorkflowServiceType[];
        requestTypes: RequestType[];
    };
}
export interface FormField {
    fieldId: string;
    label: string;
    type: 'text' | 'textarea' | 'select' | 'number' | 'date' | 'checkbox';
    required?: boolean;
    options?: string[];
    placeholder?: string;
}

/**
 * Tenant 전체 설정 조회
 */
export async function getTenantConfig(tenantId: string): Promise<TenantConfig> {
    return apiRequest<TenantConfig>(`/tenants/${tenantId}/config`);
}

// Identity 관련 타입 정의
export interface PasswordPolicy {
    minLength?: number;
    requireUppercase?: boolean;
    requireLowercase?: boolean;
    requireNumbers?: boolean;
    requireSpecialChars?: boolean;
}

export interface SessionConfig {
    timeoutSeconds?: number;
    maxConcurrentSessions?: number;
}

export interface AuthenticationMethod {
    type: string;
    enabled: boolean;
    passwordPolicyId?: string;
    provider?: string;
    issuer?: string;
}

export interface AuthenticationConfig {
    session?: SessionConfig;
    methods?: AuthenticationMethod[];
    passwordPolicy?: PasswordPolicy;
    ipWhitelist?: {
        enabled: boolean;
        allowedIpRanges?: string[];
    };
    requireMfa?: Record<string, boolean>;
}

export interface OidcConfig {
    issuer: string;
    clientId: string;
    clientSecret?: string;
    scopes?: string[];
}

export interface SamlConfig {
    idpMetadataUrl: string;
    spEntityId: string;
}

export interface IdentityProvider {
    idpId: string;
    type: 'local' | 'oidc' | 'saml';
    displayName: string;
    enabled: boolean;
    oidc?: OidcConfig;
    saml?: SamlConfig;
    mapping?: {
        userIdClaim?: string;
        emailClaim?: string;
        groupsClaim?: string;
    };
}

export interface AuditConfig {
    log_access?: boolean;
    log_changes?: boolean;
    retention_days?: number;
    alert_on_failed_access?: boolean;
    alert_threshold?: number;
}

export interface IdentityConfig {
    schemaVersion: string;
    tenantId: string;
    generatedAt: string;
    authentication?: AuthenticationConfig;
    identityProviders?: IdentityProvider[];
    audit?: AuditConfig;
}

/**
 * Tenant identity 설정 조회
 */
export async function getTenantIdentity(tenantId: string): Promise<IdentityConfig> {
    return apiRequest<IdentityConfig>(`/tenants/${tenantId}/identity`);
}

/**
 * Tenant 인증 통계 (총 사용자, 2FA 사용자 수)
 */
export async function getTenantIdentityStats(tenantId: string): Promise<{ totalUsers: number; mfaEnabledUsers: number }> {
    return apiRequest<{ totalUsers: number; mfaEnabledUsers: number }>(`/tenants/${tenantId}/identity/stats`);
}

/**
 * Tenant identity 설정 업데이트
 */
export async function updateTenantIdentity(tenantId: string, identity: Partial<IdentityConfig>): Promise<IdentityConfig> {
    return apiRequest<IdentityConfig>(`/tenants/${tenantId}/identity`, {
        method: 'PUT',
        body: JSON.stringify(identity),
    });
}

// 세션 관리
export interface TenantSession {
    sessionId: string;
    userId: string;
    tenantId: string;
    createdAt: string;
    lastActivityAt: string;
    userAgent?: string | null;
}

export async function getTenantSessions(tenantId: string): Promise<{ sessions: TenantSession[] }> {
    return apiRequest<{ sessions: TenantSession[] }>(`/tenants/${tenantId}/sessions`);
}

export async function revokeTenantSession(tenantId: string, sessionId: string): Promise<void> {
    await fetch(`${config.apiUrl}/api/portal/tenants/${tenantId}/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${getTenantAuthStorage().getToken()}`,
            'X-Tenant-Id': tenantId,
        },
    }).then(async (res) => {
        if (!res.ok) {
            const err = await res.json().catch(() => ({ message: '세션 종료 실패' }));
            throw new Error(err.message || `API Error: ${res.status}`);
        }
    });
}

export async function revokeAllTenantSessions(tenantId: string, userId?: string): Promise<{ revoked: number }> {
    return apiRequest<{ revoked: number }>(`/tenants/${tenantId}/sessions/revoke-all`, {
        method: 'POST',
        body: JSON.stringify(userId != null ? { userId } : {}),
    });
}

// 모니터링 API
export interface MonitoringMetrics {
    timestamp: string;
    host?: string;
    cpu: { usagePercent: number; cores?: number };
    memory: { usedPercent: number; usedGb: number; totalGb: number };
    disk: Array<{ mount: string; usedPercent: number; usedGb: number; totalGb: number }>;
    network: { rxBytesSec: number; txBytesSec: number };
}

export interface MonitoringService {
    name: string;
    status: 'running' | 'stopped' | 'warning';
    checks?: Record<string, unknown>;
    timestamp?: string;
}

export interface MonitoringAlert {
    id: string;
    type: string;
    severity: string;
    message: string;
    service: string | null;
    timestamp: string;
    resolved: boolean;
    duration?: string | null;
}

export interface MonitoringSummary {
    timestamp: string;
    activeServices: number;
    totalServices: number;
    uptimePercent: number;
    unresolvedAlerts: number;
    cpuPercent: number;
    memoryPercent: number;
}

export interface MonitoringPerformancePoint {
    timestamp: string;
    cpuPercent: number;
    memoryPercent: number;
    networkMbSec: number;
    uptimePercent: number;
    totalServices: number;
    downServices: number;
    alertCount: number;
    unresolvedAlerts: number;
    errorRatePercent: number;
}

export async function getMonitoringMetrics(tenantId: string): Promise<MonitoringMetrics> {
    return apiRequest<MonitoringMetrics>(`/tenants/${tenantId}/monitoring/metrics`, {
        timeout: MONITORING_REQUEST_TIMEOUT_MS,
    });
}

export async function getMonitoringServices(tenantId: string): Promise<{
    timestamp: string;
    total: number;
    ok: number;
    partial: number;
    down: number;
    services: MonitoringService[];
}> {
    return apiRequest(`/tenants/${tenantId}/monitoring/services`, {
        timeout: MONITORING_REQUEST_TIMEOUT_MS,
    });
}

export async function getMonitoringAlerts(
    tenantId: string,
    params?: { timeRange?: string; resolved?: string }
): Promise<{ alerts: MonitoringAlert[]; total: number; timeRange: string }> {
    const q = new URLSearchParams();
    if (params?.timeRange) q.set('timeRange', params.timeRange);
    if (params?.resolved !== undefined) q.set('resolved', params.resolved);
    const query = q.toString();
    return apiRequest(`/tenants/${tenantId}/monitoring/alerts${query ? `?${query}` : ''}`, {
        timeout: MONITORING_REQUEST_TIMEOUT_MS,
    });
}

export async function getMonitoringSummary(tenantId: string): Promise<MonitoringSummary> {
    return apiRequest<MonitoringSummary>(`/tenants/${tenantId}/monitoring/summary`, {
        timeout: MONITORING_REQUEST_TIMEOUT_MS,
    });
}

export async function getMonitoringPerformance(
    tenantId: string,
    params?: { limit?: number }
): Promise<{ points: MonitoringPerformancePoint[]; limit: number }> {
    const q = new URLSearchParams();
    if (params?.limit != null) q.set('limit', String(params.limit));
    const query = q.toString();
    return apiRequest(`/tenants/${tenantId}/monitoring/performance${query ? `?${query}` : ''}`, {
        timeout: MONITORING_REQUEST_TIMEOUT_MS,
    });
}

export async function resolveMonitoringAlert(tenantId: string, alertId: string): Promise<MonitoringAlert> {
    return apiRequest<MonitoringAlert>(`/tenants/${tenantId}/monitoring/alerts/${alertId}/resolve`, {
        method: 'PATCH',
        body: JSON.stringify({}),
    });
}